import { useCallback, useEffect, useRef, useState } from "react";

import type { UploadedImage } from "@/lib/item-images";

// The donor's `useItemImagesUploader` (`ItemImagesUploader.tsx`): the state
// behind the Request Item dialog's uploader, kept out of the dialog because it
// is a small machine with three moving parts — what is uploaded, what is still
// going up, and what failed.
//
// The donor's shape, preserved:
//
//   - a picked file appears **at once**, before its bytes have landed. The donor
//     pushes the row into the form right after the presign and only then awaits
//     the PUT, so the typist watches their picture arrive as they drop it;
//   - files are uploaded **as they are picked**, not at submit, so the item save
//     carries ids rather than bytes;
//   - a failure is recorded **per file name** and shown as its own message, and
//     removing the image clears it. The failed row **stays on screen**: the
//     donor's message is "Remove image and then try uploading again", and its
//     `onRemoveImage` clears the message by matching the removed file's name, so
//     a row that vanished on failure would leave the typist told to remove
//     something that is not there and no gesture that would unblock submit;
//   - the submit button is disabled while anything is uploading or any failure
//     is outstanding (`isImageUploading || imageUploadErrors.length > 0`);
//   - opening the dialog resets all three.
//
// **A row in flight, or one that failed, wears a placeholder id.** Atlas's
// presign and PUT are one call (`uploadToStorage`), so unlike the donor there is
// no catalog id to hold until the bytes are up. `attachedIds` is what the save
// reads and it names the settled rows only — which costs nothing, because submit
// is disabled for exactly as long as any row is unsettled or any failure stands.
//
// **`reset()` is a hard boundary.** It is the dialog closing, and an upload
// still in the air belongs to the draft that just went away: its image, its
// failure and its decrement of the in-flight count would otherwise land in the
// *next* draft — one of which (the decrement) would leave a fresh dialog
// believing nothing is uploading while something is. Each upload carries the
// session it was started in and no-ops if that session is over.
//
// One deliberate difference, and it is a bug fix rather than a divergence: the
// donor's `validFiles.map(async …)` fires its uploads without awaiting them and
// sets `isImageUploading` false inside *each*, so a two-file drop reports itself
// finished when the first one lands. Here the flag counts what is in flight.

export interface ItemImagesUploaderOptions {
  /** Uploads one file and answers the catalog image row it created. */
  upload: (file: File, onProgress?: (fraction: number) => void) => Promise<{ imageId: number }>;
}

export interface ItemImagesUploader {
  images: readonly UploadedImage[];
  setImages: (next: UploadedImage[]) => void;
  /** The catalog ids of the settled rows, in the order they are shown. */
  attachedIds: readonly number[];
  /**
   * Names of files whose upload failed, newest first — the donor's order.
   *
   * **Derived from the rows, not accumulated beside them.** The donor keeps a
   * second list and clears it on every new drop, which means picking one more
   * file after a failure takes the message away *and* — through
   * `imageUploadErrors.length > 0` — re-enables submit, while the failed
   * thumbnail is still sitting there and `attachedIds` quietly omits it.
   * Reading the rows makes the message and the block one fact with one
   * lifetime: both last until the row the message names is removed, which is
   * what the message tells the typist to do.
   */
  failures: readonly string[];
  /** Shorthand for `failures.length > 0` — what disables submit. */
  hasFailure: boolean;
  /**
   * Whether anything from a pick is still to land — the rows on the wire **and**
   * the queued tail behind them.
   *
   * The donor counts the whole batch at the drop
   * (`setImagesUploadingCount((prev) => prev + imagesToUpload)`,
   * `AssetEditForm.tsx:86`), so its submit is held for files that have no row
   * yet too. Read off the rows alone this let go the moment the only pending row
   * was removed, while the rest of the batch was still coming.
   */
  isUploading: boolean;
  /**
   * How far the upload in hand has got, 0–100 — the donor's single number for
   * the whole field (`MultipleImageUploadField.tsx:74-75,94-96`), which is
   * coherent precisely because uploads run one at a time.
   */
  progress: number;
  /**
   * Files taken from a pick that have no row yet — the tail of a batch still
   * waiting its turn.
   *
   * Rows appear one at a time, as each file's turn comes, so `images.length`
   * lags the number of files that are actually going to arrive. The field's cap
   * is `limit - value.length` (`MultipleImageUploadField.tsx:121,132`), and
   * read off the rows alone it counts a
   * five-file batch as one image for as long as the first upload is running:
   * pick five, pick five again, and twelve rows land under a six-image label.
   * This is what the second pick has to be measured against as well.
   */
  queued: number;
  addFiles: (files: File[]) => void;
  removeAt: (index: number) => void;
  reset: () => void;
}

export function useItemImagesUploader({ upload }: ItemImagesUploaderOptions): ItemImagesUploader {
  const [images, setImages] = useState<readonly UploadedImage[]>([]);
  const [progress, setProgress] = useState(0);
  const [queued, setQueued] = useState(0);

  // A `blob:` URL is what holds its blob alive, so a preview that is never
  // drawn again is a photo the tab keeps until navigation. Released at all three
  // points where that becomes true — the row is removed, the dialog closes, the
  // component goes away — and tracked in a ref so the last of those can reach
  // the ones the other two did not.
  const objectUrls = useRef<string[]>([]);
  const release = useCallback((url: string) => {
    URL.revokeObjectURL(url);
    objectUrls.current = objectUrls.current.filter((held) => held !== url);
  }, []);
  const releaseAll = useCallback(() => {
    for (const url of objectUrls.current) URL.revokeObjectURL(url);
    objectUrls.current = [];
  }, []);
  useEffect(() => () => releaseAll(), [releaseAll]);

  // Negative and falling, so a placeholder can never be mistaken for a catalog
  // id — including by the `id` React keys the field draws rows with. It does not
  // restart on reset: a row from the closed draft must not collide with one from
  // the new draft if a stale upload is still holding the old id.
  const nextPlaceholder = useRef(0);

  /** Which draft is on screen. Bumped by `reset()`; see the note above. */
  const session = useRef(0);

  /**
   * **One file at a time, in the order they were picked** — the donor's
   * `for (const file of event.files) { … await postImage(…) }`
   * (`AssetEditForm.tsx:88-118`), ported on the owner's ruling D4 for #300.
   * Firing the whole drop at once finished sooner but put every byte of a
   * six-image batch on the wire simultaneously, and left the field's single
   * progress number meaningless.
   *
   * **That invariant is per pick, not global — as the donor's is.** Each call
   * starts its own loop, so a pick made while an earlier batch is still
   * draining runs alongside it: two files on the wire at once, both writing the
   * one `progress` number, and rows landing interleaved rather than in pick
   * order. The donor is built the same way — `upload-images` is one `async`
   * handler fired per drop against a single shared `uploadProgressCb`
   * (`AssetEditForm.tsx:84-118`, `MultipleImageUploadField.tsx:74-75,94-96`)
   * — so funnelling every pick through one drainer would be the divergence,
   * not the fix. Raised by cross-review three times and carried to #411's
   * decision round for a ruling; the interleaving is pinned by a test below so
   * it cannot drift while it waits.
   *
   * **A row is added as its turn comes, not up front.** That is the donor's
   * order too — it pushes each image into the form between its presign and its
   * PUT — so thumbnails fill in one by one. There is no frame in between where
   * Save flickers back on: the update that settles file N and the update that
   * adds file N+1 both land in the same microtask after the `await`, so React
   * batches them into a single render.
   */
  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const startedIn = session.current;
      setQueued((waiting) => waiting + files.length);

      void (async () => {
        for (const file of files) {
          if (session.current !== startedIn) return;

          const previewUrl = URL.createObjectURL(file);
          objectUrls.current.push(previewUrl);
          const placeholderId = (nextPlaceholder.current -= 1);
          setImages((current) => [
            ...current,
            // `key` is the row's name for its whole life; `id` below is the
            // placeholder the PUT will replace. Keeping them apart is what
            // lets a drag survive its own upload finishing.
            {
              key: `picked-${placeholderId}`,
              id: placeholderId,
              previewUrl,
              fileName: file.name,
              pending: true,
            },
          ]);
          // Off the queue in the same render as the row it became.
          setQueued((waiting) => waiting - 1);
          setProgress(0);

          try {
            const { imageId } = await upload(file, (fraction) => {
              if (session.current === startedIn) setProgress(Math.ceil(fraction * 100));
            });
            if (session.current !== startedIn) return;
            setImages((current) =>
              current.map((image) =>
                // Gone already if the typist removed it mid-upload, in which
                // case there is nothing to settle and nothing to put back.
                image.id === placeholderId ? { ...image, id: imageId, pending: false } : image,
              ),
            );
          } catch {
            if (session.current !== startedIn) return;
            // The donor records the file name and leaves the rest of the drop
            // alone; the message the dialog shows names it. The row stays, so
            // the removal the message asks for is a gesture the typist has
            // (owner ruling D2 for #300 keeps this in place).
            setImages((current) =>
              current.map((image) =>
                image.id === placeholderId ? { ...image, pending: false, failed: true } : image,
              ),
            );
          }
        }
        // The bar goes with the batch, which is the donor's behaviour too: its
        // `sendXhr` calls `progressCb?.(0)` immediately after `resolve`
        // (`services/common.ts:128`), and `showProgress = progress > 0`
        // (`MultipleImageUploadField.tsx:75`) then unmounts the bar. Resetting
        // per file and resetting after the loop are observably the same here
        // because the reset and the next file's row land in one React batch.
        if (session.current === startedIn) setProgress(0);
      })();
    },
    [upload],
  );

  const removeAt = useCallback(
    (index: number) => {
      // Read from the rendered value rather than from inside the updater: a
      // state updater must be pure, and React calls it twice under StrictMode.
      const removed = images[index];
      if (removed === undefined) return;
      setImages((current) => current.filter((image) => image.id !== removed.id));
      release(removed.previewUrl);
      // Nothing else to clear: the *row* is the whole state — with the queued
      // tail as the one exception, because a file that has no row yet cannot be
      // removed. Its failure goes with it, and so does its in-flight-ness —
      // which is the donor's
      // remove-image dispatch resetting the upload count (`AssetEditForm.tsx:
      // 121-123`), and the reason `isUploading` is derived from the rows below
      // rather than counted alongside them. A separate counter did not know the
      // row had gone, so removing a still-uploading image left Save disabled
      // until its PUT settled — for good, if it hung, with nothing on screen to
      // explain it.
    },
    [images, release],
  );

  const reset = useCallback(() => {
    session.current += 1;
    // Including previews for uploads still in the air: their rows are gone, so
    // nothing will draw them whether the bytes land or not.
    releaseAll();
    setImages([]);
    setProgress(0);
    // Whatever a stale batch had left to add belongs to the draft that just
    // closed, and its loop will bail on the session check rather than draining
    // the count itself.
    setQueued(0);
  }, [releaseAll]);

  // Newest first, as the donor's `[filename, ...prev]` builds it — rows are
  // held in the order they were picked, so the reversal is the donor's order.
  const failures = images
    .filter((image) => image.failed === true)
    .map((image) => image.fileName)
    .reverse();

  return {
    images,
    setImages: (next) => setImages(next),
    attachedIds: images.filter((image) => !image.pending && !image.failed).map((image) => image.id),
    failures,
    hasFailure: failures.length > 0,
    isUploading: queued > 0 || images.some((image) => image.pending === true),
    progress,
    queued,
    addFiles,
    removeAt,
    reset,
  };
}
